import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Axios from "../../utils/axios";
import DDMultiple, { Option } from "../../components/Atomos/DropDown/DDMultiple";
import Close from "../../components/Atomos/Icons/Close";
import Input from "../../components/Atomos/Inputs/Input";
import Modal from "../../components/Atomos/Modals/Modal";
import CheckBox from "../../components/staff/CheckBox";
import { UseFirmList, firmType } from "../../hooks/Catalogs/useFirm";
import { UsePracticeIndustryList } from "../../hooks/Catalogs/usePracticeIndustry";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import { getCurrentTimestamp } from "../../utils/functions";
import { getOfficeByFirm } from "../../Services/Office";
import { toastError, toastSuccess } from "../../utils/toast";
import Button from "../../components/staff/Button";
import SaveChanges from "../../components/Atomos/Icons/EditMode/SaveChanges";
import "./StaffAopCheckboxes.css";

const ALL_FIRMS_ID = "__all_firms__";
/** Stable key for remove handlers (aligned with firm multiselect state). */
const FIRM_SELECTION_KEY = "Firm";
/** Equal-width staff actions: full width on xs; fixed width at sm+ (overrides {@link Button} `md:w-auto`). */
const FORM_FOOTER_BUTTON_CLASS =
  "w-full justify-center sm:w-[12.5rem] md:w-[12.5rem] lg:w-[12.5rem] shrink-0";
const IGNORE_MODAL_BUTTON_CLASS =
  "w-full min-w-0 justify-center !w-full sm:!w-[11.5rem] sm:shrink-0";
const axios = new Axios();

type IndustryRow = {
  id: string;
  name: string;
  isChecked?: boolean;
  icon?: string;
};

function normalizeFirmSelection(prev: Option[], next: Option[]): Option[] {
  const allOpt: Option = { id: ALL_FIRMS_ID, name: "All Firms" };
  const hasAll = (list: Option[]) => list.some((o) => String(o.id) === ALL_FIRMS_ID);
  if (!hasAll(next)) {
    return next.filter((o) => String(o.id) !== ALL_FIRMS_ID);
  }
  if (next.length === 1) {
    return [allOpt];
  }
  if (!hasAll(prev) && hasAll(next)) {
    return [allOpt];
  }
  if (hasAll(prev) && next.length > 1) {
    return next.filter((o) => String(o.id) !== ALL_FIRMS_ID);
  }
  return [allOpt];
}

async function fetchAopById(aopId: string): Promise<any | null> {
  const filter = { id: { value: [aopId], comparisonType: "IN" } };
  const params = new URLSearchParams();
  params.append("filter", JSON.stringify(filter));
  params.append("pagination", JSON.stringify({ pageIndex: 0, pageSize: 1 }));
  try {
    const res = await axios.Get(`AreaOfPractice?${params.toString()}`);
    const rows: any[] = Array.isArray(res.data) ? res.data : [];
    return rows[0] ?? null;
  } catch {
    return null;
  }
}

function firmIdsFromSelection(selected: Option[], allFirms: { id: string }[]): string[] {
  if (selected.some((o) => String(o.id) === ALL_FIRMS_ID)) {
    return allFirms.map((f) => f.id);
  }
  return selected.map((o) => String(o.id)).filter(Boolean);
}

function isFullFirmSelection(ids: string[], allFirms: { id: string }[]): boolean {
  if (allFirms.length === 0 || ids.length !== allFirms.length) {
    return false;
  }
  const set = new Set(ids);
  return allFirms.every((f) => set.has(f.id));
}

/** Firms that list this AOP on `aopIds` (canonical; AOP no longer stores `firms` on the document). */
function firmIdsLinkedToAop(
  allFirms: { id: string; aopIds?: string[] }[],
  aopId: string,
): string[] {
  if (!aopId.trim()) {
    return [];
  }
  return allFirms
    .filter((f) => Array.isArray(f.aopIds) && f.aopIds.map(String).includes(aopId))
    .map((f) => f.id)
    .filter(Boolean) as string[];
}

const AOPMappingForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();

  const isEdit = Boolean(routeId);
  const { firmData, refetchFirms } = UseFirmList(firmType.both);
  const { practiceIndustryData } = UsePracticeIndustryList();

  const locationState = location.state as Record<string, unknown> | null;
  const [aopRow, setAopRow] = useState<any | null>(() =>
    isEdit && locationState && String((locationState as any).id) === routeId ? (locationState as any) : null,
  );
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(isEdit && !(locationState && String((locationState as any).id) === routeId));

  const [aopName, setAopName] = useState("");
  const [selectedFirms, setSelectedFirms] = useState<Option[]>([]);
  const [industries, setIndustries] = useState<IndustryRow[]>([]);
  const [ignoreModalOpen, setIgnoreModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ignoring, setIgnoring] = useState(false);

  useEffect(() => {
    if (!isEdit || !routeId) {
      return;
    }
    refetchFirms();
  }, [isEdit, routeId]);

  useEffect(() => {
    if (!isEdit || !routeId) {
      return;
    }
    if (locationState && String((locationState as any).id) === routeId) {
      setAopRow(locationState as any);
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const row = await fetchAopById(routeId);
      if (cancelled) {
        return;
      }
      if (!row) {
        setLoadError(true);
        setLoading(false);
        return;
      }
      setAopRow(row);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, routeId, locationState]);

  const initialIgnored = Boolean(aopRow?.isIgnored);
  const statusMappedOrUnmapped = isEdit && !initialIgnored;

  useEffect(() => {
    if (!aopRow) {
      return;
    }
    setAopName(String(aopRow.displayName ?? aopRow.name ?? "").trim() || "");
  }, [aopRow]);

  useEffect(() => {
    if (!aopRow || !firmData.length) {
      return;
    }
    const aopId = String(aopRow.id ?? routeId ?? "").trim();
    const all = Array.isArray(firmData) ? firmData : [];
    let ids = firmIdsLinkedToAop(
      all as { id: string; aopIds?: string[] }[],
      aopId,
    );
    if (ids.length === 0 && Array.isArray(aopRow.firms) && aopRow.firms.length) {
      ids = aopRow.firms.map((f: { id?: string }) => f?.id).filter(Boolean) as string[];
    }
    if (isFullFirmSelection(ids, all as { id: string }[])) {
      setSelectedFirms([{ id: ALL_FIRMS_ID, name: "All Firms" }]);
    } else {
      setSelectedFirms(
        ids.map((fid) => {
          const f = all.find((x: { id: string }) => x.id === fid);
          return {
            id: fid,
            name: (f as any)?.name ?? (f as any)?.firmName ?? fid,
          };
        }),
      );
    }
  }, [aopRow, firmData, routeId]);

  useEffect(() => {
    if (!practiceIndustryData?.length) {
      return;
    }
    const piIds: string[] =
      (Array.isArray(aopRow?.practiceIndustryIds) && aopRow.practiceIndustryIds) ||
      (Array.isArray(aopRow?.practiceIndustries) && aopRow.practiceIndustries) ||
      [];
    const idSet = new Set(piIds.map(String));
    setIndustries(
      practiceIndustryData.map((p: { id: string; name: string; icon?: string }) => ({
        id: p.id,
        name: p.name,
        icon: p.icon,
        isChecked: idSet.has(p.id),
      })),
    );
  }, [practiceIndustryData, aopRow]);

  const pathnameLower = location.pathname.toLowerCase();
  useEffect(() => {
    const label = aopName.trim() || (isEdit ? "Edit AOP" : "Add AOP");
    const crumbs: Record<string, string> = {};
    if (pathnameLower.includes("/aop/create")) {
      crumbs["/aop/create"] = label;
    } else if (routeId) {
      crumbs["/aop/edit"] = label;
    }
    setPageMeta({
      title: aopName.trim() || (isEdit ? "AOP" : "New AOP"),
      breadcrumbLabels: crumbs,
    });
    return () => clearPageMeta();
  }, [aopName, isEdit, pathnameLower, routeId, setPageMeta, clearPageMeta]);

  const firmOptions: Option[] = useMemo(() => {
    const firms = Array.isArray(firmData) ? firmData : [];
    const rest = firms.map((f: { id: string; name?: string; firmName?: string }) => ({
      id: f.id,
      name: (f as any).name ?? (f as any).firmName ?? f.id,
    }));
    return [{ id: ALL_FIRMS_ID, name: "All Firms" }, ...rest];
  }, [firmData]);

  const setFirmsSelection = useCallback((updater: React.SetStateAction<Option[]>) => {
    setSelectedFirms((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return normalizeFirmSelection(prev, next);
    });
  }, []);

  const hasPanelFilterSelections = selectedFirms.length > 0;

  const handleRemoveSelectedValue = useCallback((_fieldName: string, valueToRemove: Option) => {
    setSelectedFirms((current) =>
      current.filter((item) => {
        if (item.id != null && valueToRemove.id != null) {
          return String(item.id) !== String(valueToRemove.id);
        }
        return item.name !== valueToRemove.name;
      }),
    );
  }, []);

  const handleClearAllSelectedFilters = useCallback(() => {
    setSelectedFirms([]);
  }, []);

  const practiceIndustryIdsSelected = useMemo(
    () => industries.filter((i) => i.isChecked).map((i) => i.id),
    [industries],
  );

  const canSave = useMemo(() => {
    if (!aopName.trim() || selectedFirms.length === 0 || practiceIndustryIdsSelected.length === 0) {
      return false;
    }
    return true;
  }, [aopName, selectedFirms.length, practiceIndustryIdsSelected.length]);

  const toggleAllPracticeIndustries = useCallback(() => {
    const allOn = industries.length > 0 && industries.every((i) => i.isChecked);
    setIndustries((prev) => prev.map((i) => ({ ...i, isChecked: !allOn })));
  }, [industries]);

  const buildFirmsPayload = async (firmIds: string[]) => {
    const pairs = await Promise.all(
      firmIds.map(async (firmId) => {
        const offices = await getOfficeByFirm(firmId);
        const office = Array.isArray(offices)
          ? offices.map((o) => o.id).filter((id): id is string => Boolean(id))
          : [];
        return { id: firmId, office };
      }),
    );
    return pairs;
  };

  const handleSave = async () => {
    if (!canSave || saving) {
      return;
    }
    const firmIds = firmIdsFromSelection(selectedFirms, firmData as { id: string }[]);
    if (firmIds.length === 0) {
      toastError("Select at least one firm.");
      return;
    }
    setSaving(true);
    try {
      const firmsPayload = await buildFirmsPayload(firmIds);
      const base = aopRow ?? {};
      const newId = (base as any).id ?? crypto.randomUUID();
      const piIds = practiceIndustryIdsSelected;
      const payload: Record<string, unknown> = {
        id: newId,
        displayName: aopName.trim(),
        practiceIndustryIds: piIds,
        firms: firmsPayload,
        isIgnored: false,
        isApproved: Boolean((base as any).isApproved),
        aliasName: Array.isArray((base as any).aliasName) ? (base as any).aliasName : [],
        createdDate:
          (base as any).createdDate !== undefined
            ? (base as any).createdDate
            : getCurrentTimestamp(),
      };
      if (isEdit && (base as any).createdAt != null) {
        (payload as any).createdAt = (base as any).createdAt;
      }
      if (isEdit) {
        await axios.Put("AreaOfPractice", payload);
        toastSuccess("AOP mapping saved.");
      } else {
        await axios.Post("AreaOfPractice", payload);
        toastSuccess("AOP created.");
      }
      try {
        await refetchFirms();
      } catch {
        /* list still refetches on /AOP mount if this fails */
      }
      navigate("/AOP");
    } catch (e: any) {
      toastError(e?.message ?? "Could not save AOP.");
    } finally {
      setSaving(false);
    }
  };

  const handleIgnore = async () => {
    if (!aopRow?.id || ignoring) {
      return;
    }
    setIgnoring(true);
    try {
      await axios.Put("AreaOfPractice", {
        id: aopRow.id,
        displayName:
          String(aopRow.displayName ?? aopRow.name ?? "").trim() || aopName.trim(),
        practiceIndustryIds: [],
        isIgnored: true,
        firms: [],
      });
      toastSuccess("AOP marked as ignored.");
      setIgnoreModalOpen(false);
      try {
        await refetchFirms();
      } catch {
        /* ignore */
      }
      navigate("/AOP");
    } catch (e: any) {
      toastError(e?.message ?? "Could not ignore AOP.");
    } finally {
      setIgnoring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center px-4 text-gray-600">
        Loading…
      </div>
    );
  }

  if (isEdit && (loadError || !aopRow)) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-gray-700">Could not load this AOP.</p>
        <Button
          className="w-full max-w-xs sm:w-[12.5rem] sm:max-w-none"
          text="Back to AOP Mapping"
          color="secondary"
          variant="filled"
          size="fixed"
          onClick={() => navigate("/AOP")}
        />
      </div>
    );
  }

  return (
    <>
      <Modal
        isModalOpen={ignoreModalOpen}
        toggleModal={() => !ignoring && setIgnoreModalOpen(false)}
        title="Ignore AOP"
        titleColor="text-secondary font-semibold text-lg"
        compact
        outerPaddingOnly
        compactBodyScrolls={false}
        className="w-full p-10 !max-w-md !overflow-visible border border-gray-200 shadow-lg sm:!max-w-lg"
        bodyClassName="!pt-0"
      >
        <div className="flex flex-col gap-4">
          <p className="text-left text-sm leading-relaxed text-[#474F56] sm:text-base">
            Are you sure you want to ignore this/these Areas of Practice? AOPs selected will not
            be mapped, but you can do it later.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button
              className={IGNORE_MODAL_BUTTON_CLASS}
              text="No, go back"
              onClick={() => !ignoring && setIgnoreModalOpen(false)}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              disabled={ignoring}
            />
            <Button
              className={IGNORE_MODAL_BUTTON_CLASS}
              text={ignoring ? "Ignoring…" : "Yes, ignore"}
              onClick={() => void handleIgnore()}
              color="secondary"
              variant="filled"
              size="fixed"
              icon={<SaveChanges />}
              disabled={ignoring}
            />
          </div>
        </div>
      </Modal>

      <div className="w-full">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
          <section className="mb-6 sm:mb-8">
            <h2 className="mb-3 text-base font-semibold text-gray-font sm:mb-4 sm:text-xl">
              Firms
            </h2>
            <div className="flex w-full flex-col gap-3 md:gap-5 px-2 md:px-4">
              <div className="w-full">
                <DDMultiple
                  title="Select Firm(s)"
                  required
                  placeholder="Select one or more"
                  objects={firmOptions}
                  selectedObjst={selectedFirms}
                  seter={setFirmsSelection}
                  disableSort
                />
              </div>
              <div className="rounded-md border border-gray-100 p-3 sm:p-4">
                <div className="flex flex-wrap items-center gap-2 gap-x-3 text-sm sm:gap-3">
                  <span className="font-semibold text-gray-font">Selected Firm(s)</span>
                  {hasPanelFilterSelections && (
                    <>
                      <span className="h-4 w-px bg-gray-300" />
                      <button
                        type="button"
                        onClick={handleClearAllSelectedFilters}
                        className="font-medium text-secondary underline hover:text-secondary-dark"
                      >
                        Clear All Filters
                      </button>
                    </>
                  )}
                </div>

                {!hasPanelFilterSelections ? (
                  <p className="mt-3 text-sm text-gray-500">No firms selected.</p>
                ) : (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                    {selectedFirms.map((value, index) => (
                      <span
                        key={`${FIRM_SELECTION_KEY}-${value.id ?? value.name ?? index}`}
                        className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-gray-font"
                      >
                        {value.name ?? String(value.id)}
                        <button
                          type="button"
                          onClick={() => handleRemoveSelectedValue(FIRM_SELECTION_KEY, value)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <Close className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="mb-3 text-base font-semibold text-[#474F56] sm:mb-4 sm:text-xl">
              Area Of Practice
            </h2>
            <div className="px-2 md:px-4">
              <Input
                title="AOP Name"
                name="aopDisplayName"
                required
                value={aopName}
                placeholder="e.g. Smith"
                onChange={(e) => setAopName(e.target.value)}
                error={false}
                isWfull
              />
            </div>
          </section>

          <section>
            <div className="mb-3 flex flex-row flex-wrap items-baseline gap-x-3 gap-y-1 sm:mb-4">
              <h2 className="shrink-0 text-base font-semibold text-[#474F56] sm:text-xl">
                Practice Industries
              </h2>
              <button
                type="button"
                className="min-w-0 text-left text-sm font-medium text-secondary hover:underline hover:text-secondary-dark"
                onClick={toggleAllPracticeIndustries}
              >
                Select / Clear All Practice Industries
              </button>
            </div>
            <div className="staff-aop-mapping-checkboxes grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 md:gap-x-6 lg:gap-x-8 px-2 md:px-4">
              {industries.map((ind) => (
                <CheckBox
                  key={ind.id}
                  className="min-w-0"
                  label={ind.name}
                  checked={Boolean(ind.isChecked)}
                  onCheckedChange={(checked) => {
                    setIndustries((prev) =>
                      prev.map((row) =>
                        row.id === ind.id ? { ...row, isChecked: checked } : row,
                      ),
                    );
                  }}
                  labelClassName="text-base leading-snug"
                />
              ))}
            </div>
          </section>

          <div
            className="mt-8 flex flex-col gap-3 border-t border-gray-100 pt-6 sm:mt-10 sm:flex-row sm:flex-wrap sm:items-stretch sm:justify-end"
          >
            {isEdit && statusMappedOrUnmapped && (
              <Button
                className={FORM_FOOTER_BUTTON_CLASS}
                text="Ignore"
                color="neutral"
                variant="outlined"
                size="fixed"
                onClick={() => setIgnoreModalOpen(true)}
                disabled={saving}
              />
            )}
            <Button
              className={FORM_FOOTER_BUTTON_CLASS}
              text="Cancel"
              onClick={() => navigate("/AOP")}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              disabled={saving || ignoring}
            />
            <Button
              className={FORM_FOOTER_BUTTON_CLASS}
              text={saving ? "Saving…" : "Save"}
              onClick={() => void handleSave()}
              color="secondary"
              variant="filled"
              size="fixed"
              icon={<SaveChanges />}
              disabled={!canSave || saving || ignoring}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AOPMappingForm;
